/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Session } from '../../lib/api/sessions.js';
import { useClients } from '../../lib/queries/clinical.js';
import { useSessions, useTransitionSession } from '../../lib/queries/sessions.js';
import { addWeeks, istDayKey } from '../../lib/utils/datetime.js';
import { MonthCalendar } from '../admin/month-calendar.js';
import { RescheduleForm } from '../admin/reschedule-form.js';
import { ScheduleCalendar } from '../admin/schedule-calendar.js';
import { SessionForm } from '../admin/session-form.js';

import { DayList, DayTabs, useWeekModel } from './day-schedule.js';
import { WeekNav } from './week-calendar.js';

type SchedView = 'list' | 'week' | 'day' | 'month';
const VIEWS: readonly SchedView[] = ['list', 'week', 'day', 'month'];

/** List / Week / Day / Month view toggle (Figma — same set as the admin schedule). */
function ViewToggle({
  view,
  onView,
}: {
  readonly view: SchedView;
  readonly onView: (v: SchedView) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const select = useCallback(
    (v: SchedView) => () => {
      onView(v);
    },
    [onView],
  );
  const cls = (active: boolean): string =>
    `focus-ring rounded-xl px-4 py-2 text-sm font-medium ${active ? 'bg-[var(--accent)] text-white' : 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'}`;
  return (
    <div className="mb-4 flex gap-2">
      {VIEWS.map((v) => (
        <button key={v} type="button" onClick={select(v)} className={cls(view === v)}>
          {t(`admin.sessions.view_${v}`)}
        </button>
      ))}
    </div>
  );
}

/** Consume the `?new=1` query param to auto-open the schedule modal once. */
function useNewSessionParam(open: () => void): void {
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      open();
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, open]);
}

/** Therapist → Schedule (Figma). Mon–Fri day tabs + the selected day's session list. */
/** Week view: nav + day tabs + the selected day's session list. */
function WeekView({
  model,
  clientNames,
  isLoading,
  isError,
  onPrev,
  onThisWeek,
  onNext,
  onSelect,
  onReschedule,
  onStart,
  startingId,
}: {
  readonly model: ReturnType<typeof useWeekModel>;
  readonly clientNames: ReadonlyMap<string, string>;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onPrev: () => void;
  readonly onThisWeek: () => void;
  readonly onNext: () => void;
  readonly onSelect: (key: string) => void;
  readonly onReschedule: (session: Session) => void;
  readonly onStart: (id: string) => void;
  readonly startingId: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <WeekNav
        rangeLabel={model.rangeLabel}
        onPrev={onPrev}
        onThisWeek={onThisWeek}
        onNext={onNext}
      />
      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('therapist.schedule.empty')}
      >
        <DayTabs
          days={model.days}
          counts={model.counts}
          selectedKey={model.activeKey}
          onSelect={onSelect}
        />
        <DayList
          sessions={model.byDay.get(model.activeKey) ?? []}
          clientNames={clientNames}
          onReschedule={onReschedule}
          onStart={onStart}
          startingId={startingId}
        />
      </QueryState>
    </>
  );
}

export function TherapistSchedulePage(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<SchedView>('list');
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [rescheduling, setRescheduling] = useState<Session | null>(null);
  const [selectedKey, setSelectedKey] = useState<string>(() => istDayKey(new Date().toISOString()));
  const sessions = useSessions();
  const clients = useClients();
  const transition = useTransitionSession();

  const clientNames = useMemo(
    () => new Map((clients.data ?? []).map((c) => [c.id, c.fullName])),
    [clients.data],
  );
  const model = useWeekModel(anchor, sessions.data, selectedKey);

  const openModal = useCallback(() => {
    setOpen(true);
  }, []);
  useNewSessionParam(openModal);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);
  const prevWeek = useCallback(() => {
    setAnchor((current) => addWeeks(current, -1));
  }, []);
  const nextWeek = useCallback(() => {
    setAnchor((current) => addWeeks(current, 1));
  }, []);
  const thisWeek = useCallback(() => {
    setAnchor(new Date());
  }, []);
  const onReschedule = useCallback((session: Session) => {
    setRescheduling(session);
  }, []);
  const onRescheduleOpenChange = useCallback((next: boolean) => {
    if (!next) {
      setRescheduling(null);
    }
  }, []);
  const closeReschedule = useCallback(() => {
    setRescheduling(null);
  }, []);
  const onStart = useCallback(
    (id: string) => {
      transition.mutate({ id, action: 'start' });
    },
    [transition],
  );

  const startingId = transition.isPending ? transition.variables.id : null;

  return (
    <div className="p-8">
      <PageHeader
        title={t('therapist.schedule.title')}
        subtitle={t('therapist.schedule.weekOf', { range: model.rangeLabel })}
        action={
          <Button onClick={openModal}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('therapist.schedule.add')}
          </Button>
        }
      />

      <ViewToggle view={view} onView={setView} />

      {view === 'month' && (
        <MonthCalendar sessions={sessions.data ?? []} clientNames={clientNames} />
      )}
      {(view === 'week' || view === 'day') && (
        <ScheduleCalendar sessions={sessions.data ?? []} clientNames={clientNames} mode={view} />
      )}
      {view === 'list' && (
        <WeekView
          model={model}
          clientNames={clientNames}
          isLoading={sessions.isLoading}
          isError={sessions.isError}
          onPrev={prevWeek}
          onThisWeek={thisWeek}
          onNext={nextWeek}
          onSelect={setSelectedKey}
          onReschedule={onReschedule}
          onStart={onStart}
          startingId={startingId}
        />
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t('therapist.schedule.add')}
        description={t('admin.sessions.scheduleSubtitle')}
        closeLabel={t('common.cancel')}
      >
        <SessionForm onDone={closeModal} hideTherapist />
      </Modal>

      <Modal
        open={rescheduling !== null}
        onOpenChange={onRescheduleOpenChange}
        title={t('therapist.sessions.rescheduleTitle')}
        description={t('therapist.sessions.rescheduleSubtitle')}
        closeLabel={t('common.cancel')}
      >
        {rescheduling !== null && (
          <RescheduleForm
            sessionId={rescheduling.id}
            currentStart={rescheduling.scheduledStart}
            onDone={closeReschedule}
          />
        )}
      </Modal>
    </div>
  );
}
